import React, { useCallback, useEffect, useMemo } from 'react';
import { groupToBeOpened, isIntentId } from '../productListHelper';

export const useItemGroups = ({
  groups = [],
  productsDisplayed,
  groupTotals,
  loadingItemIds = [],
  resolvedIntentItemId = []
}) => {
  const [groupedItems, setGroupedItems] = React.useState([]);

  const loadedItemIds = useMemo(() => {
    const __loadedItemIds = new Set();
    productsDisplayed.forEach((product) => {
      if (product?.itemId) __loadedItemIds.add(product?.itemId);
    });
    resolvedIntentItemId.forEach((__itemId) => {
      __loadedItemIds.add(__itemId);
    });
    return __loadedItemIds;
  }, [productsDisplayed, resolvedIntentItemId]);

  const isLoadingOrLoadedItem = useCallback((listItem) => {
    return (
      loadedItemIds.has(listItem.itemId || listItem.intentId)
      || loadingItemIds.has(listItem.itemId)
    );
  }, [loadedItemIds, loadingItemIds]);

  useEffect(() => {
    const __groupedItems = groups.map((group) => {
      // Use group.items (the list items) instead of group.products
      const groupItems = group.items || [];

      // Only include items that have product data loaded to prevent individual 'product' calls
      // Include items with 'id' property or intentId as they are considered loaded
      const filteredGroupItems = groupItems.filter(isLoadingOrLoadedItem);

      // Handle ungrouped items (groupId is null) - following project-details pattern
      const groupKey = group.groupId || '__ungrouped__';

      // Calculate notLoadedItems for this group - exclude intent items
      const notLoadedItems = groupItems
        .filter((listItem) => !isLoadingOrLoadedItem(listItem))
        .map((item) => item?.itemId || item?.intentId);

      // noOfItems should reflect total items (loaded + notLoaded)
      const totalItems = filteredGroupItems.length + notLoadedItems.length;
      return {
        id: groupKey || '',
        title: group.groupName || 'Default Group', // Project-details style: simple fallback
        noOfItems: totalItems,
        items: filteredGroupItems
          .map((listItem) => listItem?.itemId || listItem?.intentId)
          .filter(Boolean), // Exclude items without valid id
        notLoadedItems,
        // Server-side subtotal will be handled by GroupSubTitle component
        subTotal: (groupTotals && groupTotals[groupKey]) || 0
      };
    });

    const defaultOpenedGroupId = groupToBeOpened(__groupedItems);
    const defaultOpenedGroup = __groupedItems.find((__group) => __group.id === defaultOpenedGroupId);
    const isDefaultOpenedGroupNotLoaded = defaultOpenedGroup?.items?.length === 0;
    // Kickoff loading on some edge cases where default opened group has no loaded items
    if (defaultOpenedGroupId && isDefaultOpenedGroupNotLoaded) {
      setGroupedItems(() => {
        return __groupedItems.map((__group) => {
          if (__group.id === defaultOpenedGroupId && __group.items?.length === 0) {
            const __items = [];
            const __notLoadedItems = [...__group.notLoadedItems];
            if (isIntentId(__group.notLoadedItems[0])) {
              __items[0] = __group.notLoadedItems[0];
              __notLoadedItems.splice(0, 1);
            }
            return {
              ...__group,
              items: __items,
              notLoadedItems: __notLoadedItems
            };
          }
          return __group;
        });
      });
    } else {
      setGroupedItems(__groupedItems);
    }
  }, [groups, loadedItemIds, groupTotals, isLoadingOrLoadedItem]);

  return {
    groupedItems,
    setGroupedItems
  };
};
